// FILE: src/pages/Users/UsersPage.jsx — Admin only
import React, { useEffect, useState, useCallback } from 'react';
import { usersAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';
import './Users.css';

const ROLES    = ['Admin','CaseManager','Caregiver','Billing'];
const initials = (f,l) => `${f?.[0]||''}${l?.[0]||''}`.toUpperCase();
const colors   = ['#1a4d8c','#059669','#d97706','#7c3aed','#db2777','#0891b2'];
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'}) : '—';

export default function UsersPage() {
  // ── ALL hooks must come before any conditional return ──
  const { hasRole, user: me } = useAuth();

  const [users,     setUsers]     = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [resetPwd,  setResetPwd]  = useState(null);
  const [newPwd,    setNewPwd]    = useState('');
  const [pwdLoad,   setPwdLoad]   = useState(false);
  const [deleting,  setDeleting]  = useState(null);
  const [delLoad,   setDelLoad]   = useState(false);

  const isAdmin = hasRole('Admin');

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setLoading(true);
      const res = await usersAPI.getAll();
      setUsers(res.data.data   || []);
      setFiltered(res.data.data || []);
    } catch { toast.error('Failed to load users.'); }
    finally   { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(users); return; }
    const q = search.toLowerCase();
    setFiltered(users.filter(u =>
      `${u.FirstName} ${u.LastName}`.toLowerCase().includes(q) ||
      (u.Email||'').toLowerCase().includes(q) ||
      (u.Username||'').toLowerCase().includes(q) ||
      (u.Role||'').toLowerCase().includes(q)
    ));
  }, [search, users]);

  // ── Gate the render AFTER all hooks ──
  if (!isAdmin) {
    return (
      <div className="card">
        <p style={{color:'var(--gray-500)'}}>⛔ Admin access only.</p>
      </div>
    );
  }

  const handleToggle = async (u) => {
    try {
      await usersAPI.toggleStatus(u.ID);
      toast.success(`${u.FirstName} ${u.LastName} status updated.`);
      load();
    } catch { toast.error('Failed.'); }
  };

  const handleResetPwd = async () => {
    if (!newPwd.trim() || newPwd.length < 6) { toast.error('Password must be at least 6 characters.'); return; }
    setPwdLoad(true);
    try {
      await usersAPI.resetPassword(resetPwd.ID, { NewPassword: newPwd });
      toast.success('Password reset successfully.');
      setResetPwd(null); setNewPwd('');
    } catch { toast.error('Reset failed.'); }
    finally   { setPwdLoad(false); }
  };

  const handleDelete = async () => {
    setDelLoad(true);
    try {
      await usersAPI.delete(deleting.ID);
      toast.success('User deleted.');
      setDeleting(null); load();
    } catch (err) { toast.error(err.response?.data?.message||'Delete failed.'); }
    finally         { setDelLoad(false); }
  };

  const roleCount = (role) => users.filter(u=>u.Role===role).length;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>System Users</h1>
          <p>{users.length} total users · {users.filter(u=>u.IsActive).length} active</p>
        </div>
        <div style={{display:'flex',gap:10}}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search name, email, role…"
              value={search} onChange={e=>setSearch(e.target.value)}
              style={{paddingLeft:36,minWidth:240}}/>
          </div>
          <button className="btn btn-primary" onClick={()=>{ setEditing(null); setShowModal(true); }}>➕ Add User</button>
        </div>
      </div>

      <div className="stats-grid-4" style={{marginBottom:20}}>
        {ROLES.map(role=>(
          <div className="stat-card" key={role}>
            <div className="stat-card-header">
              <span className="stat-card-label">{role}</span>
              <span style={{fontSize:20}}>{role==='Admin'?'👑':role==='CaseManager'?'📋':role==='Caregiver'?'👨‍⚕️':'💰'}</span>
            </div>
            <div className="stat-card-value">{roleCount(role)}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{padding:0}}>
        {loading ? (
          <div className="loading-state">⏳ Loading users…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👤</div>
            <div className="empty-state-text">{search ? 'No users match.' : 'No users yet.'}</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="ec-table">
              <thead>
                <tr><th>User</th><th>Username</th><th>Role</th><th>Joined</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(u=>(
                  <tr key={u.ID}>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div className="user-avatar"
                          style={{background: u.IsActive ? colors[u.ID%colors.length] : 'var(--gray-300)'}}>
                          {initials(u.FirstName,u.LastName)}
                        </div>
                        <div>
                          <div style={{fontWeight:600,color:'var(--gray-800)'}}>{u.FirstName} {u.LastName}</div>
                          <div style={{fontSize:'0.75rem',color:'var(--gray-400)'}}>{u.Email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{fontSize:'0.82rem',color:'var(--gray-600)',fontFamily:'monospace'}}>{u.Username}</td>
                    <td>
                      <span className={`badge ${
                        u.Role==='Admin'       ? 'badge-danger'  :
                        u.Role==='CaseManager' ? 'badge-primary' :
                        u.Role==='Billing'     ? 'badge-warning' : 'badge-success'
                      }`}>{u.Role}</span>
                    </td>
                    <td style={{fontSize:'0.82rem',color:'var(--gray-500)'}}>{fmtDate(u.CreatedAt)}</td>
                    <td>
                      <span className={`badge ${u.IsActive?'badge-success':'badge-gray'}`}>
                        {u.IsActive?'Active':'Inactive'}
                      </span>
                      {u.ID === me?.id && (
                        <span className="badge badge-info" style={{marginLeft:4}}>You</span>
                      )}
                    </td>
                    <td>
                      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                        <button className="btn btn-sm btn-secondary"
                          onClick={()=>{ setEditing(u); setShowModal(true); }}>✏️</button>
                        <button className="btn btn-sm btn-secondary" title="Reset Password"
                          onClick={()=>{ setResetPwd(u); setNewPwd(''); }}>🔑</button>
                        {u.ID !== me?.id && (
                          <>
                            <button className={`btn btn-sm ${u.IsActive?'btn-secondary':'btn-success'}`}
                              onClick={()=>handleToggle(u)}>
                              {u.IsActive?'⏸️':'▶️'}
                            </button>
                            <button className="btn btn-sm btn-danger"
                              onClick={()=>setDeleting(u)}>🗑️</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <UserFormModal
          user={editing}
          onClose={()=>setShowModal(false)}
          onSaved={()=>{ setShowModal(false); load(); }}
        />
      )}

      {resetPwd && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">🔑 Reset Password</span>
              <button className="modal-close" onClick={()=>setResetPwd(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{color:'var(--gray-600)',marginBottom:14}}>
                Set new password for <strong>{resetPwd.FirstName} {resetPwd.LastName}</strong>
              </p>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input className="form-control" type="password" value={newPwd}
                  onChange={e=>setNewPwd(e.target.value)} placeholder="Min 6 characters"/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setResetPwd(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleResetPwd} disabled={pwdLoad}>
                {pwdLoad?'Resetting…':'🔑 Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleting && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">🗑️ Delete User</span>
              <button className="modal-close" onClick={()=>setDeleting(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{color:'var(--gray-600)'}}>
                Permanently delete <strong>{deleting.FirstName} {deleting.LastName}</strong>?
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setDeleting(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={delLoad}>
                {delLoad?'Deleting…':'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── User Form Modal ───────────────────────────────────────
function UserFormModal({ user, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    FirstName: user?.FirstName || '',
    LastName:  user?.LastName  || '',
    Email:     user?.Email     || '',
    Username:  user?.Username  || '',
    Role:      user?.Role      || 'Caregiver',
    Password:  '',
  });
  const [saving, setSaving] = useState(false);
  const set = (f,v) => setForm(p=>({...p,[f]:v}));

  const handleSave = async () => {
    if (!form.FirstName||!form.LastName||!form.Email||!form.Username) {
      toast.error('First name, last name, email and username are required.'); return;
    }
    if (!isEdit && !form.Password) {
      toast.error('Password is required for new users.'); return;
    }
    setSaving(true);
    try {
      if (isEdit) { await usersAPI.update(user.ID,form); toast.success('User updated.'); }
      else        { await usersAPI.create(form);         toast.success('User created.'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message||'Save failed.'); }
    finally        { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{isEdit?'✏️ Edit User':'➕ Add User'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name <span className="req">*</span></label>
              <input className="form-control" value={form.FirstName}
                onChange={e=>set('FirstName',e.target.value)} placeholder="First name"/>
            </div>
            <div className="form-group">
              <label className="form-label">Last Name <span className="req">*</span></label>
              <input className="form-control" value={form.LastName}
                onChange={e=>set('LastName',e.target.value)} placeholder="Last name"/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email <span className="req">*</span></label>
              <input className="form-control" type="email" value={form.Email}
                onChange={e=>set('Email',e.target.value)} placeholder="email@example.com"/>
            </div>
            <div className="form-group">
              <label className="form-label">Username <span className="req">*</span></label>
              <input className="form-control" value={form.Username}
                onChange={e=>set('Username',e.target.value)} placeholder="username"/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Role</label>
              <select className="form-control" value={form.Role} onChange={e=>set('Role',e.target.value)}>
                {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">
                {isEdit ? 'New Password (blank = no change)' : 'Password'}
                {!isEdit && <span className="req"> *</span>}
              </label>
              <input className="form-control" type="password" value={form.Password}
                onChange={e=>set('Password',e.target.value)} placeholder="Password"/>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving?'Saving…':isEdit?'💾 Update':'✅ Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}